import React, { useState, useEffect, useRef } from "react";
import { useReCaptcha } from "@wss/re-captcha";
import { logException } from "@wss/error-tracking";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormItem,
  Input,
  InputGroup,
} from "@clarkinc/zest-design-system/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import getPriceByEmailRequest from "./request";
import { valid } from "~/common/Email/EmailValidator";
import useIsMounted from "~/common/useIsMounted";

interface Props {
  email?: string;
  itemNumber: string;
}

const formSchema = z.object({
  emailAddress: z
    .string()
    .nonempty("Email is required.")
    .refine((value) => valid({ emailAddress: value }), {
      message: "Please enter a valid email address.",
    }),
});

const GetPriceByEmail = ({ email = "", itemNumber }: Props) => {
  const alertRef = useRef<HTMLDivElement>(null);

  const [isSuccessful, setIsSuccessful] = useState(false);

  const mounted = useIsMounted();
  const recaptcha = useReCaptcha();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      emailAddress: email,
    },
  });

  useEffect(() => {
    if (isSuccessful && alertRef.current) {
      alertRef.current.focus();
    }
  }, [isSuccessful]);

  useEffect(() => {
    if (email) {
      form.reset({ emailAddress: email ?? "" });
    }
  }, [email, form]);

  const handleSubmit = form.handleSubmit(async (data) => {
    const recaptchaResult = await recaptcha.execute(
      "PriceDiscountEmailSubmission",
    );

    if (recaptchaResult.isError) {
      setIsSuccessful(false);
      logException(recaptchaResult.error);
      return;
    }

    const result = await getPriceByEmailRequest({
      email: data.emailAddress,
      itemNumber,
      recaptchaToken: recaptchaResult.value,
    });

    if (result.success) {
      setIsSuccessful(true);
      const { emailSignupEvent } = await import("~/gtm");
      emailSignupEvent(data.emailAddress);
    } else {
      form.setError("emailAddress", {
        type: "manual",
        message: result.message ?? "There was an error submitting the form.",
      });
    }
  });

  return (
    <>
      {!isSuccessful && (
        <div className="input-append">
          <Form
            aria-label="Price by Email Form"
            data-component="getPriceByEmail"
            data-testid="getPriceByEmail"
            form={form}
            onSubmit={handleSubmit}
            theme="wss"
          >
            <input type="hidden" name="itemNumber" value={itemNumber} />
            <InputGroup
              errorMessages={form.formState.errors.emailAddress?.message ?? ""}
              groupLabel="Get Price by Email"
              hasError={!!form.formState.errors.emailAddress}
              id="emailPrice"
              inputSize="medium"
              theme="wss"
            >
              <InputGroup.Label className="pb-1">
                Get price by email
              </InputGroup.Label>
              <div className="flex flex-row items-start text-left w-full">
                <FormItem name="emailAddress">
                  <Input
                    aria-label="Input email to get price"
                    autoComplete="on"
                    className="w-full"
                    data-testid="emailAddress"
                    hideError
                    label="Get price by email"
                    hideLabel
                    value={form.watch("emailAddress")}
                    type="email"
                    onValueChange={(value) =>
                      form.setValue("emailAddress", value)
                    }
                  />
                </FormItem>
                <Button
                  className="border border-solid border-gray-500 shadow-none"
                  color="gray"
                  data-testid="emailSubmitButton"
                  disabled={!mounted}
                  size="medium"
                  theme="wss"
                  type="submit"
                >
                  Send
                </Button>
              </div>
              <InputGroup.Error className="form__error" />
            </InputGroup>
          </Form>
        </div>
      )}
      <Alert
        assertive
        className="w-auto mb-4"
        data-testid="form-sucess-alert"
        isOpen={isSuccessful}
        ref={alertRef}
        theme="wss"
        variant="success"
      >
        <AlertTitle className="text-xs leading-tight">Thank you!</AlertTitle>
        <p className="text-xs leading-tight mb-0">
          A message has been sent to the contact information you provided.
        </p>
      </Alert>
    </>
  );
};

export default GetPriceByEmail;
